'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function LearnCTASection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.15 }
        );
        const els = sectionRef?.current?.querySelectorAll('.reveal');
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section ref={sectionRef} className="relative py-16 md:py-20 bg-background overflow-hidden">
            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                <div className="reveal">
                    <div className="relative bg-primary rounded-3xl p-10 md:p-16 overflow-hidden">
                        {/* Background effects */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/10 rounded-full translate-x-1/3 -translate-y-1/3" />
                            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-white/5 rounded-full -translate-x-1/4 translate-y-1/4" />
                            <div
                                className="absolute inset-0 opacity-5"
                                style={{
                                    backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                                    backgroundSize: '32px 32px',
                                }}
                            />
                        </div>

                        <div className="relative z-10 max-w-3xl mx-auto text-center">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/80 text-sm font-semibold mb-6">
                                <span className="w-2 h-2 rounded-full bg-white animate-ping" />
                                Start learning live today
                            </span>

                            <h2 className="text-section-title font-extrabold text-white mb-4">
                                Your next live class is waiting.
                            </h2>

                            <p className="text-white/70 text-lg font-body leading-relaxed mb-8 max-w-xl mx-auto">
                                Book a live session with a real instructor, join a bootcamp, or enrol in a structured program. No recordings. Just real learning, in real time.
                            </p>

                            {/* Three paths */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-left">
                                {[
                                    { icon: '📅', title: 'Book a Live Slot', desc: 'Pick a time, pay, show up.', color: 'bg-white/10 border-white/20' },
                                    { icon: '🚀', title: 'Join a Bootcamp', desc: 'Enrol, follow the timetable, graduate.', color: 'bg-white/10 border-white/20' },
                                    { icon: '🏫', title: 'Enrol in a Program', desc: 'Full structured curriculum, live.', color: 'bg-white/10 border-white/20' },
                                ]?.map((path) => (
                                    <div key={path?.title} className={`${path?.color} border rounded-xl p-4`}>
                                        <span className="text-2xl mb-2 block">{path?.icon}</span>
                                        <p className="font-bold text-white text-sm mb-0.5">{path?.title}</p>
                                        <p className="text-white/60 text-xs font-body">{path?.desc}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Link
                                    href="/early-access"
                                    className="group inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-white text-primary rounded-full text-base font-bold hover:bg-white/95 shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    Get early access
                                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Link>
                                <Link
                                    href="/"
                                    className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white border border-white/25 rounded-full text-base font-semibold hover:bg-white/15 hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    Back to home
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
